import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Check, Info, Trash2 } from 'lucide-react'
import { AxiosError } from 'axios'
import api from '@/services/api'

// Governed reference data (register E8 / design L23). Manage the per-dimension
// lists the deal pickers should draw from, with a near-duplicate check at the
// point of entry so one entity does not fork into two spellings.

type Category = { key: string; label: string; count: number }
type TaxonomyPolicy = {
  key: string
  label: string
  governance: 'closed' | 'semi-governed' | 'open'
  point_of_miss_create: boolean
  managed_by: string
  description: string
}
type Entry = {
  id: string
  category: string
  value: string
  normalizedValue: string
  isActive: boolean
  // False for a value on a list the platform ships (a listed Capital Program).
  retirable?: boolean
  notes: string
}
type Candidate = { entry: Entry; score: number }
type MatchResult = { normalized: string; exact: Entry | null; candidates: Candidate[] }

type DuplicateBlock = {
  code: 'near_duplicate' | 'exact_duplicate'
  message: string
  candidates?: Candidate[]
  existing?: Entry
}

function isDuplicateBlock(value: unknown): value is DuplicateBlock {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    ((value as { code: string }).code === 'near_duplicate' ||
      (value as { code: string }).code === 'exact_duplicate')
  )
}

export default function ReferenceDataSettingsTab() {
  const [categories, setCategories] = useState<Category[]>([])
  const [threshold, setThreshold] = useState<number>(0.85)
  const [taxonomy, setTaxonomy] = useState<TaxonomyPolicy[]>([])
  const [selected, setSelected] = useState<string>('')
  const [entries, setEntries] = useState<Entry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [newValue, setNewValue] = useState('')
  const [live, setLive] = useState<MatchResult | null>(null)
  const [block, setBlock] = useState<DuplicateBlock | null>(null)
  const [saving, setSaving] = useState(false)
  const [savedNote, setSavedNote] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadCategories = useCallback(async () => {
    const res = await api.get<{ categories: Category[]; taxonomy: TaxonomyPolicy[]; matchThreshold: number }>(
      '/reference-data/categories',
    )
    // Broker firms/contacts use the parent/child CRM directory, not this
    // legacy flat list. Keeping it out of the editor prevents two authorities.
    const editable = res.data.categories.filter((category) => category.key !== 'broker_firm')
    setCategories(editable)
    setTaxonomy(res.data.taxonomy)
    setThreshold(res.data.matchThreshold)
    setSelected((prev) => prev || editable[0]?.key || '')
  }, [])

  const loadEntries = useCallback(async (category: string) => {
    if (!category) return
    const res = await api.get<{ entries: Entry[] }>('/reference-data', {
      params: { category },
    })
    setEntries(res.data.entries)
  }, [])

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    loadCategories()
      .catch(() => alive && setError('Unable to load reference data.'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [loadCategories])

  useEffect(() => {
    if (selected) void loadEntries(selected)
    setNewValue('')
    setLive(null)
    setBlock(null)
  }, [selected, loadEntries])

  // As-you-type near-duplicate check (debounced). Read-only: never writes.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setBlock(null)
    const value = newValue.trim()
    if (!value || !selected) {
      setLive(null)
      return
    }
    debounceRef.current = setTimeout(() => {
      api
        .post<MatchResult>('/reference-data/match', { category: selected, value })
        .then((res) => setLive(res.data))
        .catch(() => setLive(null))
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [newValue, selected])

  const selectedLabel = useMemo(
    () => categories.find((c) => c.key === selected)?.label ?? '',
    [categories, selected],
  )

  const refresh = useCallback(async () => {
    await Promise.all([loadCategories(), loadEntries(selected)])
  }, [loadCategories, loadEntries, selected])

  const submit = useCallback(
    async (force: boolean) => {
      const value = newValue.trim()
      if (!value) return
      setSaving(true)
      setError(null)
      setBlock(null)
      try {
        await api.post('/reference-data', { category: selected, value, force })
        setNewValue('')
        setLive(null)
        setSavedNote(`Added “${value}”.`)
        await refresh()
      } catch (err) {
        const detail = (err as AxiosError<{ detail?: unknown }>).response?.data?.detail
        if (isDuplicateBlock(detail)) {
          setBlock(detail)
        } else {
          setError('Could not add the entry.')
        }
      } finally {
        setSaving(false)
      }
    },
    [newValue, selected, refresh],
  )

  const retire = useCallback(
    async (entry: Entry) => {
      setError(null)
      try {
        await api.delete(`/reference-data/${entry.id}`)
        await refresh()
      } catch {
        setError('Could not retire the entry.')
      }
    },
    [refresh],
  )

  if (loading) {
    return <div className="p-4 text-[12px] text-stone-500">Loading reference data…</div>
  }

  const exactBlocked = live?.exact != null
  const liveCandidates = live?.candidates ?? []
  // A release-managed list (Capital programs) is fixed by the platform: the API
  // refuses a new value, so offering a form for one would only end in an error.
  const selectedPolicy = taxonomy.find((policy) => policy.key === selected)
  const releaseManaged = selectedPolicy?.managed_by === 'release'

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-[13px] font-medium text-stone-100">Governed Reference Data</h3>
        <p className="text-[11px] text-stone-500 mt-1">
          The controlled vocabularies the deal pickers draw from. Adding a value runs a
          near-duplicate check (similarity ≥ {threshold.toFixed(2)}) so one entity does not
          fork into two spellings. Matches are surfaced for you to confirm — nothing is merged
          automatically.
        </p>
      </div>

      <div className="rounded-lg border border-amber-400/15 bg-amber-400/[0.04] px-4 py-3 text-[12px] text-stone-300 flex gap-2">
        <Info size={14} className="mt-0.5 flex-shrink-0 text-amber-400/80" />
        <div>
          <span className="font-medium text-stone-200">Needs verification: Antarctica seed lists.</span>{' '}
          These lists ship empty by design. Seed them from Antarctica&apos;s verified broker and
          contact list once received — do not populate them with placeholder or assumed values.
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3" aria-label="Reference data governance taxonomy">
        {taxonomy.map((policy) => (
          <div key={policy.key} className="rounded-lg border border-amber-400/10 bg-black/20 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-medium text-stone-200">{policy.label}</span>
              <span className={`rounded border px-1.5 py-0.5 text-[9px] uppercase tracking-wide ${
                policy.governance === 'closed'
                  ? 'border-red-400/20 text-red-300'
                  : policy.governance === 'semi-governed'
                    ? 'border-amber-400/20 text-amber-300'
                    : 'border-emerald-400/20 text-emerald-300'
              }`}>{policy.governance}</span>
            </div>
            <p className="mt-1.5 text-[10px] leading-relaxed text-stone-500">{policy.description}</p>
            <p className="mt-2 text-[9px] text-stone-600">
              Point-of-miss creation: {policy.point_of_miss_create ? 'allowed with confirmation' : 'not allowed'}
            </p>
          </div>
        ))}
      </div>

      {error && (
        <div className="rounded border border-red-400/20 bg-red-400/10 px-3 py-2 text-[12px] text-red-300">
          {error}
        </div>
      )}
      {savedNote && (
        <div className="flex items-center gap-1.5 text-[11px] text-emerald-400">
          <Check size={12} /> {savedNote}
        </div>
      )}

      {/* Category selector */}
      <div className="flex flex-wrap gap-1.5">
        {categories.map((cat) => {
          const isActive = cat.key === selected
          return (
            <button
              key={cat.key}
              type="button"
              onClick={() => setSelected(cat.key)}
              className={`rounded-md border px-2.5 py-1.5 text-[12px] transition-all ${
                isActive
                  ? 'border-amber-400/30 bg-amber-400/[0.10] text-stone-100'
                  : 'border-amber-400/10 bg-black/20 text-stone-400 hover:text-stone-200 hover:border-amber-400/20'
              }`}
            >
              {cat.label}
              <span className="ml-1.5 text-[10px] text-stone-500">{cat.count}</span>
            </button>
          )
        })}
      </div>

      {/* Add form (a release-managed list has none: it changes with a platform release) */}
      {releaseManaged && (
        <div
          role="note"
          className="rounded-lg border border-amber-400/10 bg-black/30 px-3 py-2.5 text-[12px] text-stone-400 flex gap-2"
        >
          <Info size={14} className="mt-0.5 flex-shrink-0 text-amber-400/80" />
          <span>
            {selectedLabel} is a fixed list. Values are added with a platform release, not here
            {selectedPolicy?.description ? ` — ${selectedPolicy.description}` : '.'}
          </span>
        </div>
      )}
      {!releaseManaged && (
      <div className="rounded-lg border border-amber-400/10 bg-black/30 p-3 space-y-2">
        <label className="text-[11px] font-medium text-stone-400">Add to {selectedLabel}</label>
        <div className="flex gap-2">
          <input
            value={newValue}
            onChange={(e) => {
              setNewValue(e.target.value)
              setSavedNote(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !exactBlocked) void submit(false)
            }}
            placeholder={`New ${selectedLabel.toLowerCase().replace(/s$/, '')}…`}
            className="input flex-1 text-[12px] py-1.5"
          />
          <button
            type="button"
            className="btn-primary text-[12px]"
            disabled={saving || !newValue.trim() || exactBlocked}
            onClick={() => void submit(false)}
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
        </div>

        {/* Live (as-you-type) duplicate hint */}
        {exactBlocked && (
          <div className="flex items-center gap-1.5 text-[11px] text-amber-300">
            <AlertTriangle size={12} /> Already in this list as “{live?.exact?.value}”.
          </div>
        )}
        {!exactBlocked && liveCandidates.length > 0 && (
          <div className="text-[11px] text-stone-400">
            Possible duplicate:{' '}
            {liveCandidates
              .map((c) => `${c.entry.value} (${Math.round(c.score * 100)}%)`)
              .join(', ')}
          </div>
        )}

        {/* Blocked-on-submit resolution (near or exact duplicate) */}
        {block && (
          <div className="rounded border border-amber-400/25 bg-amber-400/[0.06] px-3 py-2 text-[12px] text-stone-200 space-y-2">
            <div className="flex items-center gap-1.5 text-amber-300">
              <AlertTriangle size={13} /> {block.message}
            </div>
            {block.candidates && block.candidates.length > 0 && (
              <ul className="space-y-1">
                {block.candidates.map((c) => (
                  <li key={c.entry.id} className="flex items-center justify-between gap-2">
                    <span className="text-stone-300">
                      {c.entry.value}{' '}
                      <span className="text-[10px] text-stone-500">
                        {Math.round(c.score * 100)}% match
                      </span>
                    </span>
                    <button
                      type="button"
                      className="text-[11px] text-amber-300 hover:text-amber-200"
                      onClick={() => {
                        setNewValue('')
                        setBlock(null)
                        setLive(null)
                      }}
                    >
                      Use this instead
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {block.code === 'near_duplicate' && (
              <button
                type="button"
                className="btn-primary text-[12px]"
                disabled={saving}
                onClick={() => void submit(true)}
              >
                Add anyway
              </button>
            )}
          </div>
        )}
      </div>
      )}

      {/* Entry list */}
      <div className="rounded-lg border border-amber-400/10 bg-black/30 overflow-x-auto">
        {entries.length === 0 ? (
          <div className="px-4 py-6 text-center text-[12px] text-stone-500">
            No {selectedLabel.toLowerCase()} recorded yet.
          </div>
        ) : (
          <table className="dense-table">
            <thead>
              <tr>
                <th>{selectedLabel}</th>
                <th className="w-64">Normalized</th>
                <th className="w-16 text-center">Retire</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="font-medium text-stone-200">{entry.value}</td>
                  <td className="text-stone-500 font-mono text-[11px]">{entry.normalizedValue}</td>
                  <td className="text-center">
                    {entry.retirable === false ? (
                      <span className="text-stone-700" title="Fixed list: changes with a platform release">
                        —
                      </span>
                    ) : (
                      <button
                        type="button"
                        title="Retire (reversible)"
                        className="text-stone-500 hover:text-red-400"
                        onClick={() => void retire(entry)}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
